import { useCallback } from "react"
import { useCopilotChat } from "@copilotkit/react-core"
import { TextMessage, Role } from "@copilotkit/runtime-client-gql"
import { useDraftingStore } from "@/features/drafting/store/drafting.store"
import { useOrderDraftStore } from "@/features/order-draft/store/order-draft.store"
import { useAnalysisStore } from "@/features/analysis/store/analysis.store"
import { useThreadContext } from "@/providers/ThreadProvider"
import { markNewAttempt } from "@/features/oio/services/draft-storage.service"
import { apiClient } from "@/services/api.client"
import { serviceForThread } from "@/services/services.registry"

export interface DraftingSessionOptions {
  /**
   * Agent whose new-attempt marker is set before the run
   * (/api/v1/storage/runs/{agentName}/{id}/new-attempt). Defaults to the active
   * thread's own agent (services.registry `runsAgentName`): "oio" for OIO and
   * legacy threads, "oia" for appeals. The marker must name the agent that
   * will actually run: the coordinator swallows a new request on a thread whose
   * latest run is `done` unless THAT agent's marker is fresh (design D3).
   */
  agentName?: string
  /**
   * draft_status written to the thread before the run starts, so a refresh
   * mid-start still shows the case as in flight. Defaults to OIO's
   * "draft_starting"; an order type with its own vocabulary passes its own
   * value so case lists label it correctly.
   */
  startingStatus?: string
}

export function useDraftingSession({
  agentName,
  startingStatus = "draft_starting",
}: DraftingSessionOptions = {}) {
  const { appendMessage, isLoading } = useCopilotChat()
  const draftingStore = useDraftingStore()
  const orderDraftStore = useOrderDraftStore()
  const analysisStore = useAnalysisStore()
  const { activeThread, setActiveThread } = useThreadContext()
  const service = serviceForThread(activeThread)
  const runsAgentName = agentName ?? service.runsAgentName

  const toErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) {
      return error.message
    }
    return `Could not start the ${service.badge} drafting run.`
  }

  const start = useCallback(
    async (prompt: string) => {
      draftingStore.reset()
      orderDraftStore.reset()
      analysisStore.reset()

      // Persist draft_starting to DB so useDraftReconnect can resume polling if
      // the page is refreshed while the draft is running. setActiveThread is
      // local-state only — after a refresh refreshThreads re-fetches from DB
      // and would return shouldPoll=false (undefined metaStatus) without this.
      if (activeThread?.id) {
        await apiClient
          .patch(`/api/v1/threads/${activeThread.id}`, { metadata: { draft_status: startingStatus } })
          .catch(() => {/* non-fatal */})
        setActiveThread({
          ...activeThread,
          metadata: { ...(activeThread.metadata ?? {}), draft_status: startingStatus },
        })
      }

      if (activeThread?.id) {
        await markNewAttempt(runsAgentName, activeThread.id)
      }

      // Do NOT wrap appendMessage in try/catch — stream/network errors from it
      // are already handled by CopilotKitProvider.onError. Catching here too
      // causes a double draftingStore.setError and shows a spurious error banner.
      await appendMessage(new TextMessage({ role: Role.User, content: prompt }))
    },
    [
      appendMessage,
      draftingStore,
      orderDraftStore,
      analysisStore,
      activeThread,
      setActiveThread,
      runsAgentName,
      startingStatus,
    ],
  )

  const resume = useCallback(
    async (checkpointId: string) => {
      draftingStore.setError({ message: "", resumeSupported: false })
      try {
        await appendMessage(
          new TextMessage({
            role: Role.User,
            content: `Resume from checkpoint ${checkpointId}`,
          }),
        )
      } catch (error) {
        draftingStore.setError({ message: toErrorMessage(error), resumeSupported: false })
        throw error
      }
    },
    [appendMessage, draftingStore],
  )

  return {
    start,
    resume,
    isActive: isLoading,
    hasError: draftingStore.error !== null,
    error: draftingStore.error,
  }
}
